import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, ChevronLeft, ChevronRight, ChevronDown, Check, X, Loader2, Save,
  PenTool, Type, Calendar, CheckSquare, User, Mail, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Set up PDF.js worker - use unpkg CDN with exact version match
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avoid using an email address as a person's signature/name. Signer records
// sometimes carry an email in the name slot (e.g. a staff account with no name);
// in that case we blank it so the signer types their real name instead.
const looksLikeEmail = (s) => typeof s === 'string' && /\S+@\S+\.\S+/.test(s.trim());
const cleanSignerName = (name) => (looksLikeEmail(name) ? '' : (name || ''));

const FIELD_ICONS = {
  signature: PenTool,
  text: Type,
  date: Calendar,
  checkbox: CheckSquare,
  initials: PenTool,
  name: User,
  email: Mail,
};

export default function PublicSigningPage() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signingData, setSigningData] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(null);
  const [adoptedSignature, setAdoptedSignature] = useState(null);
  const [adoptedInitials, setAdoptedInitials] = useState(null);

  // Fetch signing data
  useEffect(() => {
    const fetchSigningData = async () => {
      if (!requestId || !token) {
        setError('Invalid signing link');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/esignature/sign/${requestId}?token=${token}`
        );
        setSigningData(response.data);
        setPdfUrl(`${API}/esignature/sign/${requestId}/pdf?token=${token}`);
        setNumPages(response.data.page_count);
        
        // Initialize field values
        const initialValues = {};
        response.data.fields.forEach(field => {
          if (response.data.filled_values[field.id]) {
            initialValues[field.id] = response.data.filled_values[field.id];
          } else if (field.type === 'checkbox') {
            initialValues[field.id] = false;
          } else if (field.type === 'name') {
            initialValues[field.id] = cleanSignerName(response.data.signer.name);
          } else if (field.type === 'email') {
            initialValues[field.id] = response.data.signer.email;
          } else if (field.type === 'date') {
            const d = new Date();
            initialValues[field.id] = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          } else {
            initialValues[field.id] = '';
          }
        });
        // Restore any previously saved draft values so a returning signer picks up
        // where they left off ("Save & finish later").
        const draft = response.data.my_draft || {};
        Object.keys(draft).forEach((fid) => { initialValues[fid] = draft[fid]; });
        setFieldValues(initialValues);
      } catch (err) {
        console.error('Error fetching signing data:', err);
        setError(err.response?.data?.detail || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchSigningData();
  }, [requestId, token]);

  const updateFieldValue = (fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  // Clicking a signature/initials field: if already adopted, drop it in instantly;
  // otherwise open the adopt dialog.
  const handleFieldSignClick = (field) => {
    if (field.type === 'signature' && adoptedSignature) {
      updateFieldValue(field.id, adoptedSignature);
      return;
    }
    if (field.type === 'initials' && adoptedInitials) {
      updateFieldValue(field.id, adoptedInitials);
      return;
    }
    setShowSignaturePad(field.id);
  };

  // On adopt: remember the signature + initials and auto-apply to every matching
  // field so the signer never has to adopt again.
  const applyAdopted = ({ signature, initials }) => {
    if (signature) setAdoptedSignature(signature);
    if (initials) setAdoptedInitials(initials);
    let count = 0;
    setFieldValues(prev => {
      const next = { ...prev };
      (signingData?.fields || []).forEach(f => {
        if (f.type === 'signature' && signature) { next[f.id] = signature; count++; }
        else if (f.type === 'initials' && initials) { next[f.id] = initials; count++; }
      });
      return next;
    });
    setShowSignaturePad(null);
    toast.success(count > 1 ? `Applied to all ${count} signature/initial fields` : 'Signature adopted');
  };

  // Whether a checkbox group's requirement is already satisfied.
  const isGroupSatisfied = (gid) => {
    const fields = signingData?.fields || [];
    const gf = fields.filter(f => f.group_id === gid);
    if (gf.length === 0) return true;
    const first = gf[0];
    const validation = first.group_validation || 'optional';
    let required = first.group_required;
    if (required == null) required = validation !== 'optional';
    if (!required || validation === 'optional') return true;
    const checked = gf.filter(f => fieldValues[f.id] === true || fieldValues[f.id] === 'true').length;
    const total = gf.length;
    if (validation === 'select_one') return checked === 1;
    if (validation === 'select_all') return checked === total;
    const need = validation === 'select_at_least_one' ? 1 : (first.group_min || 1);
    return checked >= need;
  };

  // Ordered list of fields still needing attention (group-aware). A checkbox
  // group contributes ONE target (its first box) only while unsatisfied.
  const getIncompleteFields = (onlyRequired = false) => {
    const fields = signingData?.fields || [];
    const isEmpty = (f) => f.type === 'checkbox'
      ? !(fieldValues[f.id] === true || fieldValues[f.id] === 'true')
      : !fieldValues[f.id];
    const seenGroups = new Set();
    const result = [];
    for (const f of fields) {
      if (f.group_id) {
        if (isGroupSatisfied(f.group_id) || seenGroups.has(f.group_id)) continue;
        seenGroups.add(f.group_id);
        result.push(f);
        continue;
      }
      if (isEmpty(f) && (!onlyRequired || f.required)) result.push(f);
    }
    return result;
  };

  // Jump to the next unfilled field (page + scroll into view).
  const goToNextField = () => {
    const required = getIncompleteFields(true);
    const next = required[0] || getIncompleteFields(false)[0];
    if (!next) { toast.success('All fields are complete!'); return; }
    scrollToField(next);
  };

  // Scroll a specific field into view (all pages are rendered, so it always exists)
  // and briefly highlight it.
  const scrollToField = (field) => {
    setCurrentPage(field.page);
    setTimeout(() => {
      const el = document.querySelector(`[data-field-id="${field.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'rounded');
        setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'rounded'), 1600);
      }
    }, 120);
  };

  const handleSubmit = async () => {
    const fields = signingData.fields || [];
    // Required fields that are NOT part of a checkbox group.
    const missingFields = fields
      .filter(f => f.required && !f.group_id && !fieldValues[f.id])
      .map(f => f.label);

    // Checkbox group rules.
    const groups = {};
    fields.forEach(f => { if (f.group_id) (groups[f.group_id] = groups[f.group_id] || []).push(f); });
    const groupErrors = [];
    Object.values(groups).forEach(gf => {
      const first = gf[0];
      const validation = first.group_validation || 'optional';
      let required = first.group_required;
      if (required == null) required = validation !== 'optional';
      if (!required || validation === 'optional') return;
      const checked = gf.filter(f => fieldValues[f.id] === true || fieldValues[f.id] === 'true').length;
      const total = gf.length;
      const label = first.group_label || 'checkbox group';
      if (validation === 'select_one' && checked !== 1) groupErrors.push(`Select exactly one in "${label}"`);
      else if (validation === 'select_all' && checked !== total) groupErrors.push(`Select all ${total} in "${label}"`);
      else if (validation === 'select_at_least' || validation === 'select_at_least_one') {
        const need = validation === 'select_at_least_one' ? 1 : (first.group_min || 1);
        if (checked < need) groupErrors.push(`Select at least ${need} of ${total} in "${label}"`);
      }
    });

    const errors = [...missingFields.map(m => `Required: ${m}`), ...groupErrors];
    if (errors.length > 0) {
      const target = getIncompleteFields(true)[0];
      if (target) scrollToField(target);
      const count = errors.length;
      toast.error(
        count === 1
          ? errors[0]
          : `${count} fields need attention — jumped to the first. ${errors[0]}`
      );
      return;
    }

    setSubmitting(true);
    try {
      const fieldValuesArray = Object.entries(fieldValues).map(([field_id, value]) => ({
        field_id,
        value
      }));

      const response = await axios.post(
        `${API}/esignature/sign/${requestId}/submit?token=${token}`,
        { field_values: fieldValuesArray }
      );

      setCompleted(true);
      toast.success('Document signed successfully!');
    } catch (err) {
      console.error('Error submitting signature:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveProgress = async () => {
    setSavingProgress(true);
    try {
      await axios.post(
        `${API}/esignature/sign/${requestId}/save-progress?token=${token}`,
        { field_values: fieldValues }
      );
      toast.success('Progress saved — reopen this link anytime to finish.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save progress');
    } finally {
      setSavingProgress(false);
    }
  };

  const handleDecline = async () => {
    const reason = window.prompt('Please provide a reason for declining (optional):');
    
    try {
      await axios.post(
        `${API}/esignature/sign/${requestId}/decline?token=${token}`,
        {},
        { params: { reason } }
      );
      toast.info('You have declined to sign this document');
      setError('You have declined to sign this document');
    } catch (err) {
      toast.error('Failed to decline');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {/no longer available|already been completed/i.test(error || '') ? 'Document Unavailable' : 'Unable to Load Document'}
          </h2>
          <p className="text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Document Signed!</h2>
          <p className="text-muted-foreground mb-4">
            Thank you for signing "{signingData.request_name}". 
            All parties will be notified once everyone has signed.
          </p>
          <p className="text-sm text-muted-foreground">You can close this window.</p>
        </Card>
      </div>
    );
  }

  const allFields = signingData?.fields || [];
  const allReadonlyFields = signingData?.readonly_fields || [];
  const pageNumbers = Array.from({ length: numPages || 1 }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" />
            <div>
              <h1 className="font-semibold">{signingData?.request_name}</h1>
              <p className="text-sm text-muted-foreground">
                Signing as: {signingData?.signer?.name} ({signingData?.signer?.role})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToNextField} data-testid="next-field-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleSaveProgress} disabled={savingProgress} data-testid="save-progress-btn">
              {savingProgress ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save &amp; finish later
            </Button>
            <Button variant="outline" size="sm" onClick={handleDecline}>
              <X className="w-4 h-4 mr-1" /> Decline
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Sign Document
            </Button>
          </div>
        </div>
      </div>

      {/* Message */}
      {signingData?.message && (
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Card className="p-3 bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-800">{signingData.message}</p>
          </Card>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-4 flex gap-4">
        {/* PDF Viewer */}
        <div className="flex-1">
          {/* Page count hint */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">
              {numPages || 1} page{(numPages || 1) !== 1 ? 's' : ''} — scroll to review &amp; complete all fields
            </span>
          </div>

          {/* PDF Document with Fields — all pages stacked for continuous scroll */}
          {pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="flex items-center justify-center h-[792px]">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              }
            >
              {pageNumbers.map((pageNum) => {
                const pageFields = allFields.filter((f) => f.page === pageNum);
                const pageReadonly = allReadonlyFields.filter((f) => f.page === pageNum);
                return (
                  <div
                    key={pageNum}
                    className="relative bg-white shadow-lg mx-auto mb-4"
                    style={{ width: '612px' }}
                    data-page-container={pageNum}
                  >
                    <Page
                      pageNumber={pageNum}
                      width={612}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />

                    {/* Read-only fields completed by earlier signers / sender pre-fill */}
                    {pageReadonly.map((field) => (
                      <FieldOverlay
                        key={`ro-${field.id}`}
                        field={field}
                        value={signingData?.filled_values?.[field.id]}
                        readOnly
                        onChange={() => {}}
                        onSignatureClick={() => {}}
                      />
                    ))}

                    {/* Editable field overlays */}
                    {pageFields.map((field) => (
                      <FieldOverlay
                        key={field.id}
                        field={field}
                        value={fieldValues[field.id]}
                        onChange={(value) => updateFieldValue(field.id, value)}
                        onSignatureClick={() => handleFieldSignClick(field)}
                      />
                    ))}
                  </div>
                );
              })}
            </Document>
          )}
        </div>

        {/* Fields Sidebar — collapsible, auto-minimized */}
        <div className="w-80">
          <Card className="p-4 sticky top-20">
            {(() => {
              const sf = signingData?.fields || [];
              const done = sf.filter(f => {
                if (f.group_id) return isGroupSatisfied(f.group_id);
                return f.type === 'checkbox'
                  ? (fieldValues[f.id] === true || fieldValues[f.id] === 'true')
                  : !!fieldValues[f.id];
              }).length;
              return (
                <button
                  type="button"
                  onClick={() => setFieldsOpen(o => !o)}
                  className="w-full flex items-center justify-between gap-2"
                  data-testid="fields-panel-toggle"
                >
                  <span className="font-medium">
                    Fields to Complete <span className="text-muted-foreground">({done}/{sf.length})</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${fieldsOpen ? '' : '-rotate-90'}`} />
                </button>
              );
            })()}
            {fieldsOpen && (
            <div className="mt-4">
            {(() => {
              const allFields = signingData?.fields || [];
              const grp = {};
              allFields.forEach(f => { if (f.group_id) (grp[f.group_id] = grp[f.group_id] || []).push(f); });
              const entries = Object.entries(grp);
              if (entries.length === 0) return null;
              return (
                <div className="mb-4 space-y-2" data-testid="group-requirements">
                  {entries.map(([gid, gf]) => {
                    const first = gf[0];
                    const validation = first.group_validation || 'optional';
                    let required = first.group_required;
                    if (required == null) required = validation !== 'optional';
                    const checked = gf.filter(f => fieldValues[f.id] === true || fieldValues[f.id] === 'true').length;
                    const total = gf.length;
                    let ok = true; let text = `Optional (${total} options)`;
                    if (required && validation === 'select_one') { ok = checked === 1; text = 'Select exactly 1'; }
                    else if (required && validation === 'select_all') { ok = checked === total; text = `Select all ${total}`; }
                    else if (required) { const need = validation === 'select_at_least_one' ? 1 : (first.group_min || 1); ok = checked >= need; text = `Select at least ${need} of ${total}`; }
                    return (
                      <div key={gid} className={`p-2 rounded-md border text-xs ${required ? (ok ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200') : 'bg-gray-50 border-gray-200'}`}>
                        <div className="font-medium flex items-center gap-1">
                          {required && (ok ? <Check className="w-3 h-3 text-green-600" /> : <span className="text-amber-600 font-bold">!</span>)}
                          {first.group_label || 'Checkbox group'}
                        </div>
                        <div className="text-muted-foreground">{text} · {checked} selected</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
              {signingData?.fields?.map((field) => {
                const Icon = FIELD_ICONS[field.type] || Type;
                const isComplete = field.group_id
                  ? isGroupSatisfied(field.group_id)
                  : (field.type === 'checkbox'
                    ? (fieldValues[field.id] === true || fieldValues[field.id] === 'true')
                    : !!fieldValues[field.id]);
                
                return (
                  <div
                    key={field.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isComplete ? 'bg-green-50 border-green-200' : 'bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => scrollToField(field)}
                    data-testid={`field-jump-${field.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-4 h-4 ${isComplete ? 'text-green-600' : 'text-muted-foreground'}`} />
                      <span className="text-sm font-medium">{field.label}</span>
                      {field.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
                      {isComplete && <Check className="w-4 h-4 text-green-600 ml-auto" />}
                    </div>
                    <p className="text-xs text-muted-foreground">Page {field.page}</p>
                  </div>
                );
              })}
            </div>

            {/* Other Signers */}
            {signingData?.other_signers?.length > 0 && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-medium mb-2">Other Signers</h4>
                {signingData.other_signers.map((signer, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1">
                    <span>{signer.name} ({signer.role})</span>
                    <Badge variant={signer.status === 'signed' ? 'default' : 'secondary'}>
                      {signer.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
            </div>
            )}
          </Card>
        </div>
      </div>

      {/* Signature Pad Modal */}
      {showSignaturePad && (
        <SignaturePadModal
          defaultName={cleanSignerName(signingData?.signer?.name)}
          fieldType={(signingData?.fields || []).find(f => f.id === showSignaturePad)?.type}
          onSave={applyAdopted}
          onClose={() => setShowSignaturePad(null)}
        />
      )}
    </div>
  );
}

// Auto-fit the font so an entered value fills its field box width, but never
// shrinks below a legible floor (so signers can actually read/verify what they
// typed). On short scanned-form boxes the text may extend slightly beyond the
// box — legibility is prioritized over staying strictly inside the line.
function useAutoFitFont(value, { min = 12, max = 15 } = {}) {
  const ref = useRef(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const cw = el.clientWidth;
    if (!cw) return;
    let size = max;
    el.style.fontSize = size + 'px';
    while (size > min && el.scrollWidth > cw + 1) {
      size -= 0.5;
      el.style.fontSize = size + 'px';
    }
  }, [min, max]);
  useLayoutEffect(() => { fit(); }, [value, fit]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);
  return ref;
}

// Field Overlay Component
function FieldOverlay({ field, value, onChange, onSignatureClick, readOnly = false }) {
  const Icon = FIELD_ICONS[field.type] || Type;
  const fitRef = useAutoFitFont(typeof value === 'string' ? value : '');

  const renderInput = () => {
    if (readOnly) {
      const isImg = (field.type === 'signature' || field.type === 'initials')
        && typeof value === 'string' && value.startsWith('data:');
      if (isImg) {
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-50/70 border border-gray-200 rounded">
            <img src={value} alt="Signed" className="max-h-full max-w-full" />
          </div>
        );
      }
      if (field.type === 'checkbox') {
        return (
          <div className="w-full h-full flex items-center justify-center">
            <input type="checkbox" checked={value === true || value === 'true'} readOnly disabled className="w-5 h-5" />
          </div>
        );
      }
      return (
        <div
          ref={fitRef}
          className="w-full flex items-center px-2 bg-gray-50/70 border border-gray-200 rounded text-gray-700 overflow-visible whitespace-nowrap"
          style={{ minHeight: '20px', height: '100%' }}
        >
          {value}
        </div>
      );
    }
    switch (field.type) {
      case 'signature':
      case 'initials':
        return (
          <div
            className="w-full h-full flex items-center justify-center cursor-pointer bg-yellow-50 border-2 border-dashed border-yellow-400 rounded"
            onClick={onSignatureClick}
          >
            {value ? (
              <img src={value} alt="Signature" className="max-h-full max-w-full" />
            ) : (
              <span className="text-yellow-600 text-xs">Click to sign</span>
            )}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => onChange(e.target.checked)}
              className="w-5 h-5 cursor-pointer"
            />
          </div>
        );
      
      case 'date':
        return (
          <input
            ref={fitRef}
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-2 border rounded bg-white"
            style={{ minHeight: '22px', height: '100%' }}
          />
        );
      
      default:
        return (
          <input
            ref={fitRef}
            type={field.type === 'email' ? 'email' : 'text'}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || field.label}
            className="w-full px-2 border rounded bg-white"
            style={{ minHeight: '22px', height: '100%' }}
          />
        );
    }
  };

  return (
    <div
      className="absolute"
      data-field-id={field.id}
      style={{
        left: `${field.x}%`,
        top: `${field.y}%`,
        width: `${field.width}%`,
        height: `${field.height}%`,
      }}
      title={readOnly ? `${field.label} — completed by another signer` : undefined}
    >
      {renderInput()}
    </div>
  );
}

// Signature fonts for the "Type / Adopt" option
const SIGNATURE_FONTS = [
  { label: 'Dancing Script', family: "'Dancing Script', cursive" },
  { label: 'Great Vibes', family: "'Great Vibes', cursive" },
  { label: 'Sacramento', family: "'Sacramento', cursive" },
  { label: 'Pacifico', family: "'Pacifico', cursive" },
];

let _sigFontsInjected = false;
function ensureSignatureFonts() {
  if (_sigFontsInjected || typeof document === 'undefined') return;
  _sigFontsInjected = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&family=Great+Vibes&family=Sacramento&family=Pacifico&display=swap';
  document.head.appendChild(link);
}

// Signature Pad Modal Component — Draw or Type/Adopt
function SignaturePadModal({ onSave, onClose, defaultName = '', fieldType }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('type'); // 'draw' | 'type'
  const [typedName, setTypedName] = useState(defaultName);
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]);

  // Derive initials from a full name, e.g. "Doug Chen" -> "DC".
  const initialsFromName = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Render arbitrary text in the chosen signature font → PNG (matches drawn format).
  const renderTextPng = async (text, big = true) => {
    const t = (text || '').trim();
    if (!t) return null;
    const fontFamily = selectedFont.family;
    try {
      if (document.fonts && document.fonts.load) {
        await document.fonts.load(`64px ${fontFamily}`, t);
        await document.fonts.ready;
      }
    } catch (_) { /* fall back */ }
    const canvas = document.createElement('canvas');
    canvas.width = big ? 600 : 240;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    // Transparent background (no white fill) so the signature has no white box
    // behind it when placed on the document.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let size = big ? 64 : 80;
    ctx.font = `${size}px ${fontFamily}`;
    while (ctx.measureText(t).width > canvas.width - 40 && size > 20) {
      size -= 4;
      ctx.font = `${size}px ${fontFamily}`;
    }
    ctx.fillText(t, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL('image/png');
  };

  useEffect(() => { ensureSignatureFonts(); }, []);

  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Keep the drawing surface transparent (white pad shown via CSS) so the
    // exported PNG has a transparent background — no white box on the document.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, [mode]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Drawn signature → also generate initials from the name so initials fields auto-fill too.
  const saveSignature = async () => {
    const canvas = canvasRef.current;
    const signature = canvas.toDataURL('image/png');
    const initials = await renderTextPng(initialsFromName(typedName || defaultName), false);
    onSave({ signature, initials, name: typedName || defaultName });
  };

  // Typed/adopted → generate BOTH the full signature and initials in one step.
  const adoptTyped = async () => {
    const name = typedName.trim();
    if (!name) { toast.error('Please type your name'); return; }
    const signature = await renderTextPng(name, true);
    const initials = await renderTextPng(initialsFromName(name), false);
    onSave({ signature, initials, name });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">Add Your Signature</h3>

        {/* Mode tabs */}
        <div className="flex gap-1 mb-4 border-b">
          <button
            onClick={() => setMode('draw')}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${mode === 'draw' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
            data-testid="sig-tab-draw"
          >
            Draw
          </button>
          <button
            onClick={() => setMode('type')}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${mode === 'type' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}
            data-testid="sig-tab-type"
          >
            Type / Adopt
          </button>
        </div>

        {mode === 'draw' ? (
          <>
            <div className="border rounded-lg overflow-hidden mb-4">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full cursor-crosshair touch-none bg-white"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearCanvas}>Clear</Button>
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={saveSignature} className="flex-1" data-testid="sig-save-draw">Save Signature</Button>
            </div>
          </>
        ) : (
          <>
            <label className="block text-sm font-medium mb-1">Full name</label>
            <input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full name"
              className="w-full h-10 px-3 mb-3 rounded-md border border-input text-sm"
              data-testid="sig-type-name"
            />
            <label className="block text-sm font-medium mb-1">Style</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {SIGNATURE_FONTS.map((f) => (
                <button
                  key={f.label}
                  onClick={() => setSelectedFont(f)}
                  style={{ fontFamily: f.family }}
                  className={`h-14 rounded-lg border px-3 text-2xl truncate text-left ${selectedFont.label === f.label ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'border-input hover:bg-muted/50'}`}
                  data-testid={`sig-font-${f.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {typedName.trim() || 'Your Name'}
                </button>
              ))}
            </div>
            <div className="flex gap-3 mb-4">
              <div className="flex-1 border rounded-lg h-[120px] flex flex-col items-center justify-center bg-white overflow-hidden">
                <span className="text-[10px] uppercase text-muted-foreground tracking-wide">Signature</span>
                <span style={{ fontFamily: selectedFont.family }} className="text-4xl text-black px-3 truncate" data-testid="sig-type-preview">
                  {typedName.trim() || 'Your Name'}
                </span>
              </div>
              <div className="w-28 border rounded-lg h-[120px] flex flex-col items-center justify-center bg-white overflow-hidden">
                <span className="text-[10px] uppercase text-muted-foreground tracking-wide">Initials</span>
                <span style={{ fontFamily: selectedFont.family }} className="text-4xl text-black" data-testid="sig-initials-preview">
                  {initialsFromName(typedName) || '—'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              By adopting, this signature and initials will be applied to all your fields on this document. You agree it is the electronic representation of your signature for all purposes.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={adoptTyped} className="flex-1" data-testid="sig-adopt-btn">Adopt &amp; Sign</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
